import { useEffect, useRef, useState } from 'react';

type Props = {
  /** Accumulating actual web-search query strings from the server. */
  queries: string[];
};

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

/**
 * Cycles the live web-search queries one at a time, cross-fading between them so
 * a burst of searches reads as a gentle rotating stream rather than a growing
 * list (CONCEPT §4). Advances when a newer query arrives, or every ~2.5s.
 * Respects prefers-reduced-motion (instant swap, no fade).
 */
const SearchQueryTicker = ({ queries }: Props) => {
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const prevLenRef = useRef(0);

  // Jump to the newest query as soon as it arrives.
  useEffect(() => {
    if (queries.length > prevLenRef.current) {
      setIndex(queries.length - 1);
      setVisible(true);
    }
    prevLenRef.current = queries.length;
  }, [queries.length]);

  // Otherwise rotate through them on a timer.
  useEffect(() => {
    if (queries.length <= 1) return undefined;
    const timer = setInterval(() => {
      if (reducedMotion) {
        setIndex((i) => (i + 1) % queries.length);
        return;
      }
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % queries.length);
        setVisible(true);
      }, 250);
    }, 2500);
    return () => clearInterval(timer);
  }, [queries.length, reducedMotion]);

  if (queries.length === 0) return null;
  const current = queries[Math.min(index, queries.length - 1)];

  return (
    <div
      className={
        'yst-mt-1 yst-text-[11px] yst-italic yst-text-slate-500 ' +
        (reducedMotion
          ? ''
          : `yst-transition-opacity yst-duration-200 ${visible ? 'yst-opacity-100' : 'yst-opacity-0'}`)
      }
    >
      {current}
    </div>
  );
};

export default SearchQueryTicker;
